import { useEffect, useRef, useState } from "react";
import { DownloadSimple, X, DeviceMobile, CaretRight } from "@phosphor-icons/react";
import api from "@/lib/api";
import { EDITION } from "@/lib/edition";

const DISMISS_KEY = "ia_install_dismissed_at";
const SNOOZE_MS = 3 * 24 * 60 * 60 * 1000; // re-offer after 3 days
const BACKEND = process.env.REACT_APP_BACKEND_URL;

function platform() {
  const ua = navigator.userAgent || "";
  const iOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const android = /Android/.test(ua);
  const desktop = window.matchMedia("(min-width: 768px)").matches && window.matchMedia("(pointer: fine)").matches;
  return { iOS, android, desktop, mobile: !desktop && (iOS || android || window.matchMedia("(pointer: coarse)").matches) };
}

// Install banner.
//  • Desktop: shows a scannable QR (splink.pro) to install on a phone.
//  • Android: tap to fire the native install prompt.
//  • iOS: tap to open step-by-step "Add to Home Screen" instructions (iOS can't auto-install).
export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [qr, setQr] = useState(null);
  const [iosHelp, setIosHelp] = useState(false);
  const [plat] = useState(() => (typeof window !== "undefined" ? platform() : { desktop: true }));
  const timerRef = useRef(null);
  const killedRef = useRef(false);

  useEffect(() => {
    const standalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
    if (standalone) return;
    let dismissedAt = 0;
    try { dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0); } catch { /* ignore */ }
    if (dismissedAt && Date.now() - dismissedAt < SNOOZE_MS) return;

    const reveal = () => {
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => { if (!killedRef.current) setShow(true); }, 3500);
    };
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); reveal(); };
    if (window.deferredInstallPrompt) { setDeferred(window.deferredInstallPrompt); }
    const onInstallable = () => { if (window.deferredInstallPrompt) setDeferred(window.deferredInstallPrompt); };
    const onInstalled = () => { killedRef.current = true; clearTimeout(timerRef.current); setShow(false); setDeferred(null); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("pwa-installable", onInstallable);
    window.addEventListener("appinstalled", onInstalled);
    // Always reveal (desktop shows QR; mobile shows tap-to-install / iOS steps),
    // so iOS Safari and browsers that never fire the install event still get it.
    reveal();
    return () => {
      clearTimeout(timerRef.current);
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("pwa-installable", onInstallable);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  useEffect(() => {
    if (!show || !plat.desktop || qr) return;
    api.get(`/app/install?edition=${EDITION.id}`)
      .then((r) => setQr({ short_url: r.data.short_url, qr_png: r.data.qr_png?.startsWith("http") ? r.data.qr_png : `${BACKEND}${r.data.qr_png}` }))
      .catch(() => { /* optional */ });
  }, [show, plat.desktop, qr]);

  const dismiss = (e) => {
    e?.stopPropagation?.();
    killedRef.current = true; clearTimeout(timerRef.current);
    setShow(false); setIosHelp(false);
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
  };

  const nativeInstall = async () => {
    if (!deferred) return false;
    deferred.prompt();
    try { await deferred.userChoice; } catch { /* ignore */ }
    killedRef.current = true; clearTimeout(timerRef.current);
    setShow(false); setDeferred(null);
    return true;
  };

  // Tap behaviour on mobile: Android → native prompt; iOS → show Add-to-Home steps.
  const mobileTap = async () => {
    if (deferred) { await nativeInstall(); return; }
    if (plat.iOS) { setIosHelp(true); return; }
    window.location.assign("/install");
  };

  const brand = EDITION.brand;
  if (!show) return null;

  // ---- iOS instructions sheet ----
  if (plat.mobile && iosHelp) {
    return (
      <div data-testid="pwa-install-prompt" className="fixed inset-x-3 bottom-3 z-[60] mx-auto max-w-md rounded-2xl border border-border/60 bg-card p-4 shadow-xl" style={{ marginBottom: "env(safe-area-inset-bottom)" }}>
        <button onClick={dismiss} data-testid="pwa-install-dismiss" aria-label="Dismiss" className="absolute right-2 top-2 rounded-full p-1 text-muted-foreground hover:bg-accent"><X size={16} /></button>
        <p className="flex items-center gap-2 text-sm font-semibold"><DeviceMobile size={18} weight="fill" /> Add {brand} to your Home Screen</p>
        <ol className="mt-3 space-y-2 text-sm text-muted-foreground" data-testid="ios-install-steps">
          <li>1. Tap the <span className="font-medium text-foreground">Share</span> icon in Safari's toolbar.</li>
          <li>2. Choose <span className="font-medium text-foreground">Add to Home Screen</span>.</li>
          <li>3. Tap <span className="font-medium text-foreground">Add</span> — the {brand} icon appears on your home screen.</li>
        </ol>
        <a href="/install" className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-primary">Full install guide <CaretRight size={12} /></a>
      </div>
    );
  }

  // ---- Mobile: tappable install box ----
  if (plat.mobile) {
    return (
      <div
        data-testid="pwa-install-prompt"
        onClick={mobileTap}
        role="button"
        tabIndex={0}
        className="fixed inset-x-3 bottom-3 z-[60] mx-auto flex max-w-md cursor-pointer items-center gap-3 rounded-2xl border border-border/60 bg-card p-3 shadow-xl active:scale-[0.99]"
        style={{ marginBottom: "env(safe-area-inset-bottom)" }}
      >
        <img src="/icon-192.png" alt={brand} className="h-11 w-11 shrink-0 rounded-xl" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold leading-tight text-foreground">Install {brand}</p>
          <p className="text-xs text-muted-foreground">{plat.iOS ? "Tap here to add it to your Home Screen" : "Tap here to install the app on your phone"}</p>
        </div>
        <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground" data-testid="pwa-install-btn">
          <DownloadSimple size={14} weight="bold" /> Install
        </span>
        <button onClick={dismiss} data-testid="pwa-install-dismiss" aria-label="Dismiss" className="shrink-0 rounded-full p-1 text-muted-foreground hover:bg-accent"><X size={16} /></button>
      </div>
    );
  }

  // ---- Desktop: QR to scan + optional self-install ----
  return (
    <div data-testid="pwa-install-prompt" className="fixed bottom-4 right-4 z-[60] w-[340px] rounded-2xl border border-border/60 bg-card p-3 shadow-xl">
      <button onClick={dismiss} data-testid="pwa-install-dismiss" aria-label="Dismiss" className="absolute right-2 top-2 rounded-full p-1 text-muted-foreground hover:bg-accent"><X size={16} /></button>
      <div className="flex items-start gap-3">
        <img src="/icon-192.png" alt={brand} className="h-11 w-11 shrink-0 rounded-xl" />
        <div className="min-w-0 flex-1 pr-5">
          <p className="text-sm font-semibold leading-tight text-foreground">Install {brand}</p>
          <p className="text-xs text-muted-foreground">Get the app for faster access to your alumni network.</p>
        </div>
      </div>
      <div className="mt-3 flex items-center gap-3 rounded-xl bg-muted/50 p-3">
        <div className="grid h-[92px] w-[92px] shrink-0 place-items-center rounded-lg bg-white p-1">
          {qr ? <img src={qr.qr_png} alt="Install QR code" className="h-full w-full object-contain" data-testid="pwa-install-qr" />
              : <DeviceMobile size={28} className="text-muted-foreground/40" />}
        </div>
        <div className="min-w-0">
          <p className="flex items-center gap-1 text-xs font-semibold text-foreground"><DeviceMobile size={14} weight="fill" /> Scan to install on your phone</p>
          <p className="mt-1 text-[11px] leading-snug text-muted-foreground">Open your phone camera and point it at this code.</p>
          {qr?.short_url && <p className="mt-1 break-all text-[11px] font-medium text-primary">{qr.short_url}</p>}
        </div>
      </div>
      <div className="mt-3 flex gap-2">
        {deferred && (
          <button onClick={nativeInstall} data-testid="pwa-install-btn" className="inline-flex flex-1 items-center justify-center gap-1 rounded-full bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground">
            <DownloadSimple size={14} weight="bold" /> Install on this computer
          </button>
        )}
        <a href="/install" className={`inline-flex items-center justify-center rounded-full border border-border px-3 py-2 text-xs font-medium text-foreground hover:bg-accent ${deferred ? "" : "flex-1"}`} data-testid="pwa-install-more">More options</a>
      </div>
    </div>
  );
}
